import React, { useState, useRef, useEffect } from 'react'
import type { ConstructorGrid, ViewMode, GridPlacement, PreviewPlacement } from '../../types'
import { GridWord } from './GridWord'
import { useCrosswordSettings } from '../../contexts/CrosswordSettingsContext'
import { gridControls } from '../../lib/Z'
import { getCellSize } from '../../lib/gridSizing'
import { getLetterArray, getLettersFromText } from '../../lib/pairings'

type GridDisplayProps = {
  grid: ConstructorGrid
  viewMode: ViewMode
  selectedCell: { row: number; col: number } | null
  selectedPlacementIds: ReadonlySet<string>
  previewPlacement?: PreviewPlacement | null
  // Multi-placement preview (e.g. backwards-search solutions). Renders each
  // entry as a semi-transparent ghost; the entry with role 'target' gets the
  // standard preview accent, 'helper' entries are toned down.
  previewPlacements?: ReadonlyArray<PreviewPlacement>

  zoom: number
  pan: { x: number; y: number }
  placementIssueCounts?: Record<string, number>
  placementIssueLabels?: Record<string, string[]>
  listNames?: { A: string; B: string }
  // When set, GridDisplay starts dragging this placement from given pointer position
  externalDragPlacementId?: string | null
  externalDragStartPos?: { x: number; y: number } | null
  onExternalDragEnd?: () => void
  onSelectCell: (row: number, col: number) => void
  onSelectPlacement: (placementId: string | null, additive: boolean) => void
  onDropOnCell: (row: number, col: number) => void
  onToggleBlocked: (row: number, col: number) => void
  onMovePlacements: (updates: Array<{ placementId: string; newRow: number; newCol: number }>) => void
  onRotatePlacement: (placementId: string) => void
  onZoomChange: (zoom: number) => void
  onPanChange: (pan: { x: number; y: number }) => void
  onResize?: (width: number, height: number, rowOffset: number, colOffset: number) => void
  onDragStateChange?: (isDragging: boolean) => void
  onUpdatePlacementLetter?: (placementId: string, letterIndex: number, newLetter: string, side: 'A' | 'B') => void
  onDeselect?: () => void
  onSaveTempWord?: (placementId: string, side: 'A' | 'B') => void
  highlightedWords?: Set<string> | undefined
  scratchpadMode?: boolean
}

type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se'

type ResizeDragState = {
  corner: ResizeCorner
  startX: number
  startY: number
  startWidth: number
  startHeight: number
}


// Drag state stored in ref to avoid re-renders during drag
type DragState = {
  placementId: string
  startX: number
  startY: number
  originalRow: number
  originalCol: number
  pointerId: number
  clickedLetterIndex: number  // Which letter in the word was clicked
  direction: 'across' | 'down'  // Direction of the placement
  // All other placements being dragged together with this one
  extras: Array<{ placementId: string; originalRow: number; originalCol: number }>
}


export function GridDisplay({
  grid,
  viewMode,
  selectedCell,
  selectedPlacementIds,
  previewPlacement,
  previewPlacements,
  zoom,
  pan,
  placementIssueCounts = {},
  placementIssueLabels = {},
  listNames = { A: 'A', B: 'B' },
  externalDragPlacementId,
  externalDragStartPos,
  onExternalDragEnd,
  onSelectCell,
  onSelectPlacement,
  onDropOnCell,
  onToggleBlocked,
  onMovePlacements,
  onRotatePlacement,
  onZoomChange,
  onPanChange,
  onResize,
  onDragStateChange,
  onUpdatePlacementLetter,
  onDeselect,
  onSaveTempWord,
  highlightedWords,
  scratchpadMode = false
}: GridDisplayProps) {
  const settings = useCrosswordSettings()
  const cellSize = getCellSize(settings.size)

  const [overlaySide, setOverlaySide] = useState<'A' | 'B'>('A')
  const [activeSplitSide, setActiveSplitSide] = useState<'A' | 'B'>('A')
  const [isPanning, setIsPanning] = useState(false)
  const panStartRef = useRef({ x: 0, y: 0 })
  const panRef = useRef<{ x: number; y: number }>(pan)
  const lastCommittedPanRef = useRef<{ x: number; y: number }>(pan)
  const isWheelPanningRef = useRef(false)
  const wheelCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Drag state - position during drag for visual feedback. The primary placement
  // is the one the pointer is on; extras are other selected placements being moved
  // with it as a group.
  const [dragPosition, setDragPosition] = useState<{ row: number; col: number } | null>(null)
  const [draggingPlacementId, setDraggingPlacementId] = useState<string | null>(null)
  const [extraDragPositions, setExtraDragPositions] = useState<ReadonlyMap<string, { row: number; col: number }>>(new Map())
  const dragRef = useRef<DragState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const singleTransformRef = useRef<HTMLDivElement>(null)
  const splitATransformRef = useRef<HTMLDivElement>(null)
  const splitBTransformRef = useRef<HTMLDivElement>(null)
  const setSingleTransformNode = (node: HTMLDivElement | null) => {
    singleTransformRef.current = node
    gridContainerRef.current = node
  }

  // Resize drag state
  const resizeDragRef = useRef<ResizeDragState | null>(null)
  const [resizePreview, setResizePreview] = useState<{ width: number; height: number; corner: ResizeCorner } | null>(null)
  const [showResizeAnchors, setShowResizeAnchors] = useState(false)
  const [_hoveredCorner, setHoveredCorner] = useState<ResizeCorner | null>(null)
  const hideAnchorsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Positions for primary + extras during a drag, used both for visual rendering and conflict computation.
  const getDragOverridePosition = (placement: GridPlacement): { row: number; col: number } | null => {
    if (placement.id === draggingPlacementId && dragPosition) {
      return dragPosition
    }
    const extra = extraDragPositions.get(placement.id)
    if (extra) return extra
    return null
  }

  // Derived per render — single source of truth, no stale-ref hazard.
  // Cheap enough to recompute (drag overrides only mutate placement positions, not the loops).
  const cellLetterMap = new Map<string, Array<{ placementId: string; letterA: string; letterB: string }>>()
  const sources: GridPlacement[] = grid.placements.map(p => {
    const override = p.id === draggingPlacementId && dragPosition
      ? dragPosition
      : extraDragPositions.get(p.id) ?? null
    return override ? { ...p, row: override.row, col: override.col } : p
  })
  for (const placement of sources) {
    if (!placement.wordAText || !placement.wordBText) continue
    const lettersA = getLetterArray(placement.wordAText)
    const lettersB = getLetterArray(placement.wordBText)
    for (let i = 0; i < lettersA.length; i++) {
      const r = placement.direction === 'down' ? placement.row + i : placement.row
      const c = placement.direction === 'across' ? placement.col + i : placement.col
      const key = `${r},${c}`
      const entries = cellLetterMap.get(key)
      const letterA = lettersA[i]
      const letterB = lettersB[i]
      if (letterA == null || letterB == null) continue
      const entry = { placementId: placement.id, letterA, letterB }
      if (entries) entries.push(entry)
      else cellLetterMap.set(key, [entry])
    }
  }

  const conflictIndicesByPlacementId = new Map<string, number[]>()
  for (const placement of sources) {
    if (!placement.wordAText) continue
    const indices: number[] = []
    const letters = getLettersFromText(placement.wordAText)
    for (let i = 0; i < letters.length; i++) {
      const r = placement.direction === 'down' ? placement.row + i : placement.row
      const c = placement.direction === 'across' ? placement.col + i : placement.col
      const cellEntries = cellLetterMap.get(`${r},${c}`)
      if (cellEntries && cellEntries.length > 1) {
        const uniqueA = new Set(cellEntries.map(e => e.letterA))
        const uniqueB = new Set(cellEntries.map(e => e.letterB))
        if (uniqueA.size > 1 || uniqueB.size > 1) indices.push(i)
      }
    }
    conflictIndicesByPlacementId.set(placement.id, indices)
  }

  const cellToPlacementIds = new Map<string, string[]>()
  for (const placement of grid.placements) {
    if (!placement.wordAText || !placement.wordBText) continue
    const len = getLettersFromText(placement.wordAText).length
    for (let i = 0; i < len; i++) {
      const r = placement.direction === 'down' ? placement.row + i : placement.row
      const c = placement.direction === 'across' ? placement.col + i : placement.col
      const key = `${r},${c}`
      const ids = cellToPlacementIds.get(key)
      if (ids) ids.push(placement.id)
      else cellToPlacementIds.set(key, [placement.id])
    }
  }

  const getConflictIndices = (placement: GridPlacement): number[] => {
    return conflictIndicesByPlacementId.get(placement.id) ?? []
  }


  const applyPanTransform = (nextPan: { x: number; y: number }) => {
    const transform = `translate(${nextPan.x}px, ${nextPan.y}px) scale(${zoom})`
    if (singleTransformRef.current) {
      singleTransformRef.current.style.transform = transform
    }
    if (splitATransformRef.current) {
      splitATransformRef.current.style.transform = transform
    }
    if (splitBTransformRef.current) {
      splitBTransformRef.current.style.transform = transform
    }
  }

  const updatePanVisual = (nextPan: { x: number; y: number }) => {
    panRef.current = nextPan
    applyPanTransform(nextPan)
  }

  const commitPanIfChanged = () => {
    const current = panRef.current
    const lastCommitted = lastCommittedPanRef.current
    if (current.x === lastCommitted.x && current.y === lastCommitted.y) return
    lastCommittedPanRef.current = current
    onPanChange(current)
  }

  // Keep local pan/transform in sync when parent pan changes from controls (fit/reset/etc)
  useEffect(() => {
    if (isPanning || isWheelPanningRef.current) return
    panRef.current = pan
    lastCommittedPanRef.current = pan
    applyPanTransform(pan)
  }, [pan.x, pan.y])

  useEffect(() => {
    // Ensure transform updates on zoom changes even when pan doesn't change.
    applyPanTransform(panRef.current)
  }, [zoom])

  useEffect(() => {
    return () => {
      if (wheelCommitTimeoutRef.current) {
        clearTimeout(wheelCommitTimeoutRef.current)
      }
    }
  }, [])

  // Wheel handling - zoom with Ctrl/Cmd, pan otherwise (trackpad support)
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      const newZoom = Math.max(0.25, Math.min(3, zoom + delta))
      onZoomChange(newZoom)
    } else {
      // Two-finger scroll = pan
      e.preventDefault()
      isWheelPanningRef.current = true
      const currentPan = panRef.current
      const nextPan = {
        x: currentPan.x - e.deltaX,
        y: currentPan.y - e.deltaY
      }
      updatePanVisual(nextPan)

      if (wheelCommitTimeoutRef.current) {
        clearTimeout(wheelCommitTimeoutRef.current)
      }
      wheelCommitTimeoutRef.current = setTimeout(() => {
        isWheelPanningRef.current = false
        commitPanIfChanged()
      }, 120)
    }
  }

  // Pan handling with mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault()
      setIsPanning(true)
      const currentPan = panRef.current
      panStartRef.current = { x: e.clientX - currentPan.x, y: e.clientY - currentPan.y }
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      updatePanVisual({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y
      })
    }
  }

  const handleMouseUp = () => {
    if (isPanning) {
      commitPanIfChanged()
    }
    setIsPanning(false)
  }

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isPanning) {
        commitPanIfChanged()
      }
      setIsPanning(false)
    }
    window.addEventListener('mouseup', handleGlobalMouseUp)
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
  }, [isPanning])

  // Keyboard input is now handled by contentEditable in GridLetter

  // Prevent native macOS trackpad gestures (pinch to zoom, swipe to go back/forward)
  // This must use native event listeners with { passive: false } to allow preventDefault
  // See: https://github.com/micho/jQuery.preventMacBackScroll
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Prevent Safari gesture events (pinch to zoom)
    const preventGesture = (e: Event) => {
      e.preventDefault()
    }

    // Prevent wheel events from triggering browser navigation or zoom
    // React's onWheel is passive by default, so we need native listener with { passive: false }
    const preventWheel = (e: WheelEvent) => {
      e.preventDefault()
    }

    // Document-level handler to catch navigation gestures that bubble up
    // This is needed because macOS back/forward detection can happen at document level
    const preventDocumentWheel = (e: WheelEvent) => {
      if (container.contains(e.target as Node)) {
        e.preventDefault()
      }
    }

    // These events are Safari-specific and must be added via addEventListener
    container.addEventListener('gesturestart', preventGesture)
    container.addEventListener('gesturechange', preventGesture)
    container.addEventListener('gestureend', preventGesture)

    // Wheel event on container - must be non-passive
    container.addEventListener('wheel', preventWheel, { passive: false })

    // Also add at document level to catch events that might bypass the container
    document.addEventListener('wheel', preventDocumentWheel, { passive: false })

    return () => {
      container.removeEventListener('gesturestart', preventGesture)
      container.removeEventListener('gesturechange', preventGesture)
      container.removeEventListener('gestureend', preventGesture)
      container.removeEventListener('wheel', preventWheel)
      document.removeEventListener('wheel', preventDocumentWheel)
    }
  }, [])

  // Handle external drag (from palette) - uses document-level pointer tracking
  // Use ref to track current drag position to avoid stale closure issues
  const externalDragPosRef = useRef<{ row: number; col: number } | null>(null)

  // Use ref for screenToGridCell to avoid stale closures in event handlers
  const screenToGridCellRef = useRef<(clientX: number, clientY: number) => { row: number; col: number }>(() => ({ row: 0, col: 0 }))

  // Helper to convert screen coordinates to grid cell coordinates
  const screenToGridCell = (clientX: number, clientY: number): { row: number; col: number } => {
    if (!containerRef.current) return { row: 0, col: 0 }

    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top

    // In split view, each half has its own centered grid
    // We need to determine which half we're in and calculate relative to that half's center
    let centerX: number
    const centerY = rect.height / 2

    if (viewMode === 'side-by-side') {
      // In split view: left half (0 to width/2), right half (width/2 to width)
      const isLeftHalf = x < rect.width / 2
      if (isLeftHalf) {
        centerX = rect.width / 4  // Center of left half
      } else {
        centerX = rect.width * 3 / 4  // Center of right half
      }
    } else {
      centerX = rect.width / 2
    }

    // The grid (with extended bounds) is centered at (centerX, centerY)
    // Calculate where the pointer is relative to the grid's top-left corner
    const currentPan = panRef.current
    const gridTopLeftX = centerX - (gridWidth * zoom) / 2 + currentPan.x
    const gridTopLeftY = centerY - (gridHeight * zoom) / 2 + currentPan.y

    // Convert screen position to position within the grid (in pixels, accounting for zoom)
    const gridPixelX = (x - gridTopLeftX) / zoom
    const gridPixelY = (y - gridTopLeftY) / zoom

    // Convert pixel position to cell coordinates
    // The grid renders cell (extendedBounds.minRow, extendedBounds.minCol) at position (0, 0)
    // So we need to add the offset back to get actual cell coordinates
    const col = Math.round(gridPixelX / (cellSize + 1)) + extendedBounds.minCol
    const row = Math.round(gridPixelY / (cellSize + 1)) + extendedBounds.minRow

    return { row, col }
  }

  // Keep ref updated with current function
  screenToGridCellRef.current = screenToGridCell

  useEffect(() => {
    if (!externalDragPlacementId) return

    const placement = grid.placements.find(p => p.id === externalDragPlacementId)
    if (!placement) return

    // Calculate initial position from pointer or use placement position
    const initialCell = externalDragStartPos
      ? screenToGridCellRef.current(externalDragStartPos.x, externalDragStartPos.y)
      : { row: placement.row, col: placement.col }

    // Set up as if we're dragging this placement
    setDraggingPlacementId(externalDragPlacementId)
    setDragPosition(initialCell)
    externalDragPosRef.current = initialCell
    onSelectPlacement(externalDragPlacementId, false)

    const handlePointerMove = (e: PointerEvent) => {
      const cell = screenToGridCellRef.current(e.clientX, e.clientY)
      setDragPosition(cell)
      externalDragPosRef.current = cell
    }

    const handlePointerUp = () => {
      const currentDragPos = externalDragPosRef.current
      if (currentDragPos && externalDragPlacementId) {
        onMovePlacements([{ placementId: externalDragPlacementId, newRow: currentDragPos.row, newCol: currentDragPos.col }])
        // Select the first cell of the placement so user can type
        onSelectCell(currentDragPos.row, currentDragPos.col)
      }

      setDraggingPlacementId(null)
      setDragPosition(null)
      externalDragPosRef.current = null
      onExternalDragEnd?.()
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
    }
  }, [externalDragPlacementId, externalDragStartPos, grid.placements, zoom, cellSize, grid.width, grid.height, viewMode, onMovePlacements, onSelectPlacement, onSelectCell, onExternalDragEnd])

  // Cell click handler (for empty cells - on background layer)
  const handleCellClick = (row: number, col: number, e: React.MouseEvent) => {
    if (e.shiftKey) {
      onToggleBlocked(row, col)
      return
    }
    onSelectPlacement(null, false)
    onSelectCell(row, col)
  }

  // Pointer-based drag handlers for placements
  const handlePlacementPointerDown = (
    e: React.PointerEvent,
    placement: GridPlacement,
    side: 'A' | 'B'
  ) => {
    // Ignore if alt key (panning) or not primary button
    if (e.altKey || e.button !== 0) return

    e.preventDefault()
    e.stopPropagation()
    if (viewMode === 'side-by-side') {
      setActiveSplitSide(side)
    }

    // shift / cmd-click toggles selection without starting a drag
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onSelectPlacement(placement.id, true)
      return
    }

    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)

    // Calculate which cell was clicked based on pointer position within the word
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const localX = (e.clientX - rect.left) / zoom  // Account for zoom
    const localY = (e.clientY - rect.top) / zoom
    const cellWithGap = cellSize + 1

    // Determine which letter index was clicked
    const wordLength = placement.wordAText?.replace(/\s/g, '').length ?? 0
    let letterIndex = placement.direction === 'across'
      ? Math.floor(localX / cellWithGap)
      : Math.floor(localY / cellWithGap)

    // Clamp to valid range
    letterIndex = Math.max(0, Math.min(wordLength - 1, letterIndex))

    // Calculate the actual cell coordinates
    const clickedRow = placement.direction === 'down' ? placement.row + letterIndex : placement.row
    const clickedCol = placement.direction === 'across' ? placement.col + letterIndex : placement.col

    // Select the cell under the clicked letter
    onSelectCell(clickedRow, clickedCol)

    // If user clicks an already-selected placement, drag the whole selection together;
    // otherwise replace selection with just this one and drag only it.
    const dragWholeSelection = selectedPlacementIds.has(placement.id) && selectedPlacementIds.size > 1
    if (!dragWholeSelection) {
      onSelectPlacement(placement.id, false)
    }

    const extras = dragWholeSelection
      ? grid.placements
          .filter(p => p.id !== placement.id && selectedPlacementIds.has(p.id))
          .map(p => ({ placementId: p.id, originalRow: p.row, originalCol: p.col }))
      : []

    // Notify parent that drag is starting (to prevent sync during drag)
    onDragStateChange?.(true)

    dragRef.current = {
      placementId: placement.id,
      startX: e.clientX,
      startY: e.clientY,
      originalRow: placement.row,
      originalCol: placement.col,
      pointerId: e.pointerId,
      clickedLetterIndex: letterIndex,
      direction: placement.direction,
      extras,
    }
    setDraggingPlacementId(placement.id)
    setDragPosition({ row: placement.row, col: placement.col })
    setExtraDragPositions(new Map(extras.map(x => [x.placementId, { row: x.originalRow, col: x.originalCol }])))
  }

  const handlePlacementPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return

    const dx = (e.clientX - dragRef.current.startX) / zoom
    const dy = (e.clientY - dragRef.current.startY) / zoom

    // Account for cell size plus 1px gap between cells
    const cellWithGap = cellSize + 1
    const snappedCol = Math.round(dragRef.current.originalCol + dx / cellWithGap)
    const snappedRow = Math.round(dragRef.current.originalRow + dy / cellWithGap)
    const deltaRow = snappedRow - dragRef.current.originalRow
    const deltaCol = snappedCol - dragRef.current.originalCol

    // Allow placement anywhere - no clamping to grid bounds
    setDragPosition({ row: snappedRow, col: snappedCol })
    if (dragRef.current.extras.length > 0) {
      setExtraDragPositions(new Map(
        dragRef.current.extras.map(x => [
          x.placementId,
          { row: x.originalRow + deltaRow, col: x.originalCol + deltaCol }
        ])
      ))
    }

    // Update selected cell to follow the dragged placement
    const { clickedLetterIndex, direction } = dragRef.current
    const newCellRow = direction === 'down' ? snappedRow + clickedLetterIndex : snappedRow
    const newCellCol = direction === 'across' ? snappedCol + clickedLetterIndex : snappedCol
    onSelectCell(newCellRow, newCellCol)
  }

  const handlePlacementPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return

    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)

    // Notify parent that drag is ending (to allow sync)
    onDragStateChange?.(false)

    if (dragPosition) {
      const drag = dragRef.current
      const moved = dragPosition.row !== drag.originalRow || dragPosition.col !== drag.originalCol
      if (moved) {
        const updates: Array<{ placementId: string; newRow: number; newCol: number }> = [
          { placementId: drag.placementId, newRow: dragPosition.row, newCol: dragPosition.col }
        ]
        const deltaRow = dragPosition.row - drag.originalRow
        const deltaCol = dragPosition.col - drag.originalCol
        for (const x of drag.extras) {
          updates.push({
            placementId: x.placementId,
            newRow: x.originalRow + deltaRow,
            newCol: x.originalCol + deltaCol,
          })
        }
        onMovePlacements(updates)
      }
    }

    dragRef.current = null
    setDraggingPlacementId(null)
    setDragPosition(null)
    setExtraDragPositions(new Map())
  }

  const handlePlacementDoubleClick = (e: React.MouseEvent, placementId: string) => {
    e.preventDefault()
    e.stopPropagation()
    onRotatePlacement(placementId)
  }

  // Handle drag over for palette drops
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()

    // Calculate which cell was dropped on
    if (!gridContainerRef.current) return

    const rect = gridContainerRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / zoom
    const y = (e.clientY - rect.top) / zoom

    // Account for 1px gap between cells (consistent with other drag handlers)
    const cellWithGap = cellSize + 1
    const col = Math.floor(x / cellWithGap) + extendedBounds.minCol
    const row = Math.floor(y / cellWithGap) + extendedBounds.minRow

    if (scratchpadMode || (row >= 0 && row < grid.height && col >= 0 && col < grid.width)) {
      onDropOnCell(row, col)
    }
  }

  // Resize anchor handlers
  const handleResizePointerDown = (e: React.PointerEvent, corner: ResizeCorner) => {
    if (!onResize) return
    e.preventDefault()
    e.stopPropagation()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)

    resizeDragRef.current = {
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: grid.width,
      startHeight: grid.height
    }
    setResizePreview({ width: grid.width, height: grid.height, corner })
  }

  const handleResizePointerMove = (e: React.PointerEvent) => {
    if (!resizeDragRef.current) return

    const { corner, startX, startY, startWidth, startHeight } = resizeDragRef.current
    const cellWithGap = cellSize + 1

    // Calculate delta in cells (accounting for zoom)
    const dx = (e.clientX - startX) / zoom
    const dy = (e.clientY - startY) / zoom
    const deltaCols = Math.round(dx / cellWithGap)
    const deltaRows = Math.round(dy / cellWithGap)

    let newWidth = startWidth
    let newHeight = startHeight

    // Adjust dimensions based on which corner is being dragged
    switch (corner) {
      case 'se': // bottom-right: expand/contract both dimensions
        newWidth = Math.max(5, startWidth + deltaCols)
        newHeight = Math.max(5, startHeight + deltaRows)
        break
      case 'sw': // bottom-left: expand/contract height, inverse for width
        newWidth = Math.max(5, startWidth - deltaCols)
        newHeight = Math.max(5, startHeight + deltaRows)
        break
      case 'ne': // top-right: expand/contract width, inverse for height
        newWidth = Math.max(5, startWidth + deltaCols)
        newHeight = Math.max(5, startHeight - deltaRows)
        break
      case 'nw': // top-left: inverse for both
        newWidth = Math.max(5, startWidth - deltaCols)
        newHeight = Math.max(5, startHeight - deltaRows)
        break
    }

    setResizePreview({ width: newWidth, height: newHeight, corner })
  }

  const handleResizePointerUp = (e: React.PointerEvent) => {
    if (!resizeDragRef.current || !resizePreview || !onResize) return
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)

    // Calculate row/col offset based on which corner was dragged
    // When dragging nw/ne/sw corners, the grid origin shifts and placements need adjustment
    const widthDelta = resizePreview.width - grid.width
    const heightDelta = resizePreview.height - grid.height
    let rowOffset = 0
    let colOffset = 0
    switch (resizePreview.corner) {
      case 'nw': // top-left: both dimensions shift
        rowOffset = heightDelta
        colOffset = widthDelta
        break
      case 'ne': // top-right: only height shifts
        rowOffset = heightDelta
        break
      case 'sw': // bottom-left: only width shifts
        colOffset = widthDelta
        break
      case 'se': // bottom-right: no shift needed
        break
    }

    onResize(resizePreview.width, resizePreview.height, rowOffset, colOffset)
    resizeDragRef.current = null
    setResizePreview(null)
  }

  // Extended bounds — derived synchronously per render. Computing this in a
  // useEffect+ref left the visual one render behind: render N read stale
  // bounds, useEffect updated the ref after, but nothing triggered render N+1
  // — so the grid stayed out of sync after moving placements.
  const extendedBounds = (() => {
    let minRow = 0
    let maxRow = grid.height - 1
    let minCol = 0
    let maxCol = grid.width - 1

    for (const placement of grid.placements) {
      if (!placement.wordAText) continue
      const wordLen = placement.wordAText.replace(/\s/g, '').length
      const endRow = placement.direction === 'down' ? placement.row + wordLen - 1 : placement.row
      const endCol = placement.direction === 'across' ? placement.col + wordLen - 1 : placement.col

      minRow = Math.min(minRow, placement.row)
      maxRow = Math.max(maxRow, endRow)
      minCol = Math.min(minCol, placement.col)
      maxCol = Math.max(maxCol, endCol)
    }

    const padding = 25
    return {
      minRow: minRow - padding,
      maxRow: maxRow + padding,
      minCol: minCol - padding,
      maxCol: maxCol + padding,
    }
  })()

  // Calculate grid dimensions including extended area
  const extendedWidth = (extendedBounds.maxCol - extendedBounds.minCol + 1) * cellSize + (extendedBounds.maxCol - extendedBounds.minCol)
  const extendedHeight = (extendedBounds.maxRow - extendedBounds.minRow + 1) * cellSize + (extendedBounds.maxRow - extendedBounds.minRow)

  const gridWidth = extendedWidth
  const gridHeight = extendedHeight

  // Render faint background grid for extended area using CSS pattern instead of thousands of DOM elements
  const renderExtendedGridBackground = () => {
    const cellWithGap = cellSize + 1
    const lineColor = 'rgba(209, 213, 219, 0.15)'
    // Trailing gridlines (gray at the END of each cell) so the extended bg's
    // gridlines align with the official grid's gridlines (which are also trailing).
    const bgStyle = {
      backgroundImage:
        `repeating-linear-gradient(90deg, transparent 0, transparent ${cellSize}px, ${lineColor} ${cellSize}px, ${lineColor} ${cellWithGap}px),` +
        ` repeating-linear-gradient(180deg, transparent 0, transparent ${cellSize}px, ${lineColor} ${cellSize}px, ${lineColor} ${cellWithGap}px)`,
      backgroundSize: `${cellWithGap}px ${cellWithGap}px`,
    }

    if (scratchpadMode) {
      return (
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            width: (extendedBounds.maxCol - extendedBounds.minCol + 1) * cellWithGap,
            height: (extendedBounds.maxRow - extendedBounds.minRow + 1) * cellWithGap,
            ...bgStyle,
          }}
        />
      )
    }

    // Clip out the official grid area so the pattern only shows outside
    const officialLeft = (0 - extendedBounds.minCol) * cellWithGap
    const officialTop = (0 - extendedBounds.minRow) * cellWithGap
    const officialWidth = grid.width * cellWithGap
    const officialHeight = grid.height * cellWithGap

    return (
      <div
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          width: (extendedBounds.maxCol - extendedBounds.minCol + 1) * cellWithGap,
          height: (extendedBounds.maxRow - extendedBounds.minRow + 1) * cellWithGap,
          ...bgStyle,
          clipPath: `polygon(
            0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
            ${officialLeft}px ${officialTop}px,
            ${officialLeft}px ${officialTop + officialHeight}px,
            ${officialLeft + officialWidth}px ${officialTop + officialHeight}px,
            ${officialLeft + officialWidth}px ${officialTop}px,
            ${officialLeft}px ${officialTop}px
          )`,
        }}
      />
    )
  }

  // Render cell backgrounds for official grid only.
  //
  // The default white cells are drawn as a single CSS-patterned div (one node
  // for the whole grid) instead of one <div> per cell. A 70x70 grid would
  // otherwise create ~4900 DOM nodes per render, which dominates frame time.
  // Only special-state cells (selected, blocked, part of a selected placement)
  // are rendered as overlay divs on top of the patterned background.
  const renderCellBackgrounds = (_showSide: 'A' | 'B' = 'A', isActiveSelection: boolean = true) => {
    if (scratchpadMode) {
      return null
    }

    const cellWithGap = cellSize + 1
    const officialLeft = (0 - extendedBounds.minCol) * cellWithGap
    const officialTop = (0 - extendedBounds.minRow) * cellWithGap
    const officialWidth = grid.width * cellWithGap
    const officialHeight = grid.height * cellWithGap

    // gray-300 — matches outline color used by the previous per-cell divs.
    const lineColor = 'rgb(209, 213, 219)'
    // White cells with a 1px gray gridline AFTER each cell (trailing). 180deg
    // (top-to-bottom) is needed for horizontal lines so the first gray sits at
    // y=cellSize (below row 0), not at y=0 (which would draw on top of the leading-edge div).
    const patternBg =
      `repeating-linear-gradient(90deg, transparent 0, transparent ${cellSize}px, ${lineColor} ${cellSize}px, ${lineColor} ${cellWithGap}px),` +
      ` repeating-linear-gradient(180deg, transparent 0, transparent ${cellSize}px, ${lineColor} ${cellSize}px, ${lineColor} ${cellWithGap}px)`

    const overlays: React.ReactNode[] = []

    // Selected cell overlay
    if (selectedCell &&
        selectedCell.row >= 0 && selectedCell.row < grid.height &&
        selectedCell.col >= 0 && selectedCell.col < grid.width) {
      const cell = grid.cells[selectedCell.row]?.[selectedCell.col]
      if (!cell?.isBlocked) {
        const placementIds = cellToPlacementIds.get(`${selectedCell.row},${selectedCell.col}`)
        const hasPlacement = placementIds !== undefined && placementIds.length > 0
        const left = (selectedCell.col - extendedBounds.minCol) * cellWithGap
        const top = (selectedCell.row - extendedBounds.minRow) * cellWithGap
        const selectionBg = isActiveSelection ? 'bg-blue-100' : 'bg-blue-50'
        overlays.push(
          <div
            key={`sel-${selectedCell.row},${selectedCell.col}`}
            data-grid-cell
            className={`absolute ${selectionBg} outline outline-gray-200`}
            style={{ left, top, width: cellSize, height: cellSize, pointerEvents: hasPlacement ? 'none' : 'auto' }}
            onClick={(e) => !hasPlacement && handleCellClick(selectedCell.row, selectedCell.col, e)}
          />
        )
      }
    }

    // Selected-placement cell highlights. The actual placement letters render
    // separately in renderPlacements (with the drag-override applied), so this
    // overlay is purely a visual indicator of "these cells are part of the
    // selected word" — no need to redraw the letters here. We just paint a
    // soft tint behind the cells the selected placement covers.
    for (const placement of sources) {
      if (!selectedPlacementIds.has(placement.id)) continue
      if (!placement.wordAText || !placement.wordBText) continue
      const len = getLettersFromText(placement.wordAText).length
      for (let i = 0; i < len; i++) {
        const r = placement.direction === 'down' ? placement.row + i : placement.row
        const c = placement.direction === 'across' ? placement.col + i : placement.col
        if (r < 0 || r >= grid.height || c < 0 || c >= grid.width) continue
        if (selectedCell?.row === r && selectedCell?.col === c) continue
        const left = (c - extendedBounds.minCol) * cellWithGap
        const top = (r - extendedBounds.minRow) * cellWithGap
        const placementBg = isActiveSelection ? 'bg-blue-50/40' : 'bg-blue-50/20'
        overlays.push(
          <div
            key={`plc-${r},${c}`}
            data-grid-cell
            className={`absolute ${placementBg}`}
            style={{ left, top, width: cellSize, height: cellSize, pointerEvents: 'none', zIndex: 0 }}
          />
        )
      }
    }

    // Blocked cell overlays
    for (let r = 0; r < grid.height; r++) {
      const row = grid.cells[r]
      if (!row) continue
      for (let c = 0; c < grid.width; c++) {
        if (!row[c]?.isBlocked) continue
        const left = (c - extendedBounds.minCol) * cellWithGap
        const top = (r - extendedBounds.minRow) * cellWithGap
        overlays.push(
          <div
            key={`blk-${r},${c}`}
            data-grid-cell
            className="absolute bg-gray-800 cursor-pointer outline outline-gray-200"
            style={{ left, top, width: cellSize, height: cellSize }}
            onClick={(e) => handleCellClick(r, c, e)}
          />
        )
      }
    }

    return (
      <div className="absolute top-0 left-0">
        <div
          data-grid-cell
          className="absolute bg-white cursor-pointer"
          style={{
            left: officialLeft,
            top: officialTop,
            width: officialWidth,
            height: officialHeight,
            backgroundImage: patternBg,
          }}
          onClick={(e) => {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
            const x = (e.clientX - rect.left) / zoom
            const y = (e.clientY - rect.top) / zoom
            const col = Math.floor(x / cellWithGap)
            const row = Math.floor(y / cellWithGap)
            if (row >= 0 && row < grid.height && col >= 0 && col < grid.width) {
              handleCellClick(row, col, e)
            }
          }}
        />
        {/* Leading top/left gridlines — original cells' outlines extended 1px
            outside their box, so the topmost/leftmost lines sat at -1.
            The pattern only draws lines AFTER each cell, so we add them here. */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: officialLeft - 1,
            top: officialTop - 1,
            width: officialWidth + 1,
            height: 1,
            background: lineColor,
          }}
        />
        <div
          className="absolute pointer-events-none"
          style={{
            left: officialLeft - 1,
            top: officialTop - 1,
            width: 1,
            height: officialHeight + 1,
            background: lineColor,
          }}
        />
        {overlays}
      </div>
    )
  }

  const handleScratchpadLayerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scratchpadMode) return
    if (e.defaultPrevented) return
    const rect = e.currentTarget.getBoundingClientRect()
    const localX = (e.clientX - rect.left) / zoom
    const localY = (e.clientY - rect.top) / zoom
    const cellWithGap = cellSize + 1
    const col = Math.floor(localX / cellWithGap) + extendedBounds.minCol
    const row = Math.floor(localY / cellWithGap) + extendedBounds.minRow
    onSelectPlacement(null, false)
    onSelectCell(row, col)
  }

  const renderScratchpadInteractionLayer = () => {
    if (!scratchpadMode) return null
    const cellWithGap = cellSize + 1
    return (
      <div
        data-grid-cell
        className="absolute top-0 left-0 cursor-crosshair"
        style={{
          width: (extendedBounds.maxCol - extendedBounds.minCol + 1) * cellWithGap,
          height: (extendedBounds.maxRow - extendedBounds.minRow + 1) * cellWithGap,
        }}
        onClick={handleScratchpadLayerClick}
      />
    )
  }



  // Calculate position within the extended grid (accounting for offset)
  const calculateExtendedGridPosition = (row: number, col: number) => {
    return {
      x: (col - extendedBounds.minCol) * cellSize + (col - extendedBounds.minCol),
      y: (row - extendedBounds.minRow) * cellSize + (row - extendedBounds.minRow),
    }
  }

  // Render placements as absolutely positioned, directly interactive components
  const renderPlacements = (showSide: 'A' | 'B' = 'A', isActiveSelection: boolean = true) => (
    <div className="absolute top-0 left-0">
      {grid.placements.map((placement) => {
        const isSelected = selectedPlacementIds.has(placement.id)
        const dragOverride = getDragOverridePosition(placement)
        const isDragging = dragOverride !== null
        const issueCount = placementIssueCounts[placement.id] ?? 0
        const issueLabels = placementIssueLabels[placement.id] ?? []
        const issueTooltip = issueCount > 0
          ? issueLabels.length > 0
            ? `Issues (${issueCount}):\n- ${issueLabels.join('\n- ')}`
            : `${issueCount} quality issue${issueCount === 1 ? '' : 's'}`
          : undefined

        // Use drag position if this placement is being dragged (primary or extra in a group drag)
        const displayRow = dragOverride?.row ?? placement.row
        const displayCol = dragOverride?.col ?? placement.col

        const conflictIndices = getConflictIndices({ ...placement, row: displayRow, col: displayCol })
        const XY = calculateExtendedGridPosition(displayRow, displayCol)


        // Determine which text to show
        const showB = showSide === 'B'
        const wordText = showB ? placement.wordBText : placement.wordAText

        const wordLength = Math.max(placement.wordAText?.replace(/\s/g, '').length ?? 0, placement.wordBText?.replace(/\s/g, '').length ?? 0)

        // For vertical words, we need to render rotated

        // Calculate editableIndex if selected cell is within this placement
        // Only show in active grid (not the inactive split view side)
        let cursorIndex: number | null = null
        if (selectedCell && isActiveSelection) {
          const lettersA = placement.wordAText?.replace(/\s/g, '') ?? ''
          const lettersB = placement.wordBText?.replace(/\s/g, '') ?? ''
          const placementLength = Math.max(lettersA.length, lettersB.length)
          const lettersToCheck = showB ? lettersB : lettersA
          const isTempSide = showB ? placement.isTempB : placement.isTempA

          // Check if selected cell is within this placement's bounds
          if (placement.direction === 'across') {
            if (selectedCell.row === displayRow &&
                selectedCell.col >= displayCol &&
                selectedCell.col < displayCol + placementLength) {
              const idx = selectedCell.col - displayCol
              // Editable if this side has a placeholder OR is temp
              if (lettersToCheck[idx] === '□' || isTempSide) {
                cursorIndex = idx
              }
            }
          } else {
            if (selectedCell.col === displayCol &&
                selectedCell.row >= displayRow &&
                selectedCell.row < displayRow + placementLength) {
              const idx = selectedCell.row - displayRow
              // Editable if this side has a placeholder OR is temp
              if (lettersToCheck[idx] === '□' || isTempSide) {
                cursorIndex = idx
              }
            }
          }
        }

        // Determine if this side is temporary
        const isTemp = showB ? placement.isTempB : placement.isTempA

        // Handle letter input from contentEditable
        const handleLetterInput = (letterIndex: number, value: string) => {
          if (!onUpdatePlacementLetter) return
          const side = showB ? 'B' : 'A'
          onUpdatePlacementLetter(placement.id, letterIndex, value, side)

          if (value === '□') {
            // Delete: move back to previous cell
            if (letterIndex > 0) {
              const prevRow = placement.direction === 'down' ? placement.row + letterIndex - 1 : placement.row
              const prevCol = placement.direction === 'across' ? placement.col + letterIndex - 1 : placement.col
              onSelectCell(prevRow, prevCol)
            }
          } else {
            // Typing: advance to next cell
            const nextRow = placement.direction === 'down' ? placement.row + letterIndex + 1 : placement.row
            const nextCol = placement.direction === 'across' ? placement.col + letterIndex + 1 : placement.col
            if (nextRow < grid.height && nextCol < grid.width) {
              onSelectCell(nextRow, nextCol)
            }
          }
        }

        const currentSideText = showB ? placement.wordBText : placement.wordAText
        const isHighlighted = highlightedWords !== undefined && highlightedWords.size > 0 &&
          highlightedWords.has(getLettersFromText(currentSideText))

        return (
          <GridWord
            key={placement.id}
            XY={XY}
            placement={placement}
            text={wordText}
            wordLength={wordLength}
            cellSize={cellSize}
            isSelected={isSelected}
            isDragging={isDragging}
            isHighlighted={isHighlighted}
            issueCount={issueCount}
            issueTooltip={issueTooltip}
            conflictIndices={conflictIndices}
            editableIndex={cursorIndex}
            isTemp={isTemp}
            onLetterInput={handleLetterInput}
            onPointerDown={(e) => handlePlacementPointerDown(e, placement, showSide)}
            onPointerMove={handlePlacementPointerMove}
            onPointerUp={handlePlacementPointerUp}
            onDoubleClick={(e) => handlePlacementDoubleClick(e, placement.id)}
          />
        )
      })}
    </div>
  )

  type RunningCellMap = Map<string, Array<{ placementId: string; letterA: string; letterB: string }>>

  // Get conflict indices for a preview placement (which letter positions would
  // conflict). Defaults to checking against the live grid; pass a running map
  // to also include letters introduced by earlier previews in a multi-step
  // chain (so a helper that depends on a previous helper's letters is treated
  // as compatible, not conflicted).
  const getPreviewConflictIndices = (preview: PreviewPlacement, mapToCheck: RunningCellMap = cellLetterMap): number[] => {
    const indices: number[] = []
    if (!preview.wordAText || !preview.wordBText) return indices
    const lettersA = getLetterArray(preview.wordAText)
    const lettersB = getLetterArray(preview.wordBText)

    for (let i = 0; i < lettersA.length; i++) {
      const r = preview.direction === 'down' ? preview.row + i : preview.row
      const c = preview.direction === 'across' ? preview.col + i : preview.col
      const key = `${r},${c}`
      const cellEntries = mapToCheck.get(key)

      if (cellEntries && cellEntries.length > 0) {
        // Check if preview letters match existing letters
        const previewA = lettersA[i]
        const previewB = lettersB[i]
        const existingA = cellEntries[0]?.letterA
        const existingB = cellEntries[0]?.letterB

        if ((previewA && existingA && previewA !== existingA) ||
          (previewB && existingB && previewB !== existingB)) {
          indices.push(i)
        }
      }
    }
    return indices
  }

  // Render preview placement(s) (semi-transparent). Falls back to a single
  // `previewPlacement` when `previewPlacements` isn't provided.
  const renderPreviewPlacement = (showSide: 'A' | 'B' = 'A') => {
    const previews = previewPlacements && previewPlacements.length > 0
      ? previewPlacements
      : (previewPlacement ? [previewPlacement] : [])
    if (previews.length === 0) return null

    // Running map: starts as a shallow copy of the grid's cellLetterMap, and
    // gets the letters of earlier preview entries layered on. So if helper A
    // writes 'M' at (3,4) and helper B's letter at (3,4) is also 'M', helper
    // B doesn't show as a conflict.
    const runningMap: RunningCellMap = new Map()
    for (const [key, entries] of cellLetterMap) runningMap.set(key, entries)

    const ghosts: React.ReactNode[] = []

    for (let idx = 0; idx < previews.length; idx++) {
      const preview = previews[idx]!
      const XY = calculateExtendedGridPosition(preview.row, preview.col)
      const wordText = showSide === 'B' ? preview.wordBText : preview.wordAText

      const wordLength = Math.max(
        preview.wordAText?.replace(/\s/g, '').length ?? 0,
        preview.wordBText?.replace(/\s/g, '').length ?? 0,
      )

      const fakePlacement: GridPlacement = {
        id: `preview-${idx}`,
        pairingId: null,
        wordAText: preview.wordAText,
        wordBText: preview.wordBText,
        row: preview.row,
        col: preview.col,
        direction: preview.direction,
      }

      const conflictIndices = getPreviewConflictIndices(preview, runningMap)
      const hasConflicts = conflictIndices.length > 0

      // Helper steps render dimmer than the destination. Single-placement
      // previews (no role) get the original opacity behaviour.
      const role = preview.role
      const baseOpacity = role === 'helper' ? 0.35 : (role === 'target' ? 0.6 : 0.5)
      const opacity = hasConflicts ? Math.min(0.85, baseOpacity + 0.2) : baseOpacity

      ghosts.push(
        <div
          key={`preview-ghost-${idx}`}
          className="absolute top-0 left-0 pointer-events-none"
          style={{ opacity }}
        >
          <GridWord
            key={`preview-${idx}`}
            XY={XY}
            placement={fakePlacement}
            text={wordText}
            wordLength={wordLength}
            cellSize={cellSize}
            isSelected={false}
            isDragging={false}
            conflictIndices={conflictIndices}
            isPreview={true}
          />
        </div>,
      )

      // Layer this preview's letters into the running map so subsequent
      // entries see them.
      const lettersA = getLetterArray(preview.wordAText)
      const lettersB = getLetterArray(preview.wordBText)
      for (let i = 0; i < lettersA.length; i++) {
        const r = preview.direction === 'down' ? preview.row + i : preview.row
        const c = preview.direction === 'across' ? preview.col + i : preview.col
        const key = `${r},${c}`
        if (!runningMap.has(key)) {
          const letterA = lettersA[i]
          const letterB = lettersB[i]
          if (letterA && letterB) {
            runningMap.set(key, [{ placementId: `preview-${idx}`, letterA, letterB }])
          }
        }
      }
    }

    return <>{ghosts}</>
  }

  // Render resize anchors at grid corners
  const renderResizeAnchors = () => {
    if (!onResize || scratchpadMode) return null

    const anchorSize = 8
    const hoverAreaSize = 24
    const cellWithGap = cellSize + 1

    // Calculate the offset for the preview box based on which corner is being dragged
    // The dragged corner should stay in place while the opposite corner moves
    const getPreviewOffset = () => {
      if (!resizePreview) return { x: 0, y: 0 }
      const widthDelta = resizePreview.width - grid.width
      const heightDelta = resizePreview.height - grid.height
      switch (resizePreview.corner) {
        case 'nw': return { x: -widthDelta * cellWithGap, y: -heightDelta * cellWithGap }
        case 'ne': return { x: 0, y: -heightDelta * cellWithGap }
        case 'sw': return { x: -widthDelta * cellWithGap, y: 0 }
        case 'se': return { x: 0, y: 0 }
      }
    }

    const previewOffset = getPreviewOffset()

    // Base positions for the current grid corners (static, for hover areas)
    const baseLeft = (0 - extendedBounds.minCol) * cellWithGap
    const baseTop = (0 - extendedBounds.minRow) * cellWithGap
    const baseRight = (grid.width - extendedBounds.minCol) * cellWithGap - 1
    const baseBottom = (grid.height - extendedBounds.minRow) * cellWithGap - 1

    // Preview positions (adjusted during drag)
    const previewLeft = resizePreview ? baseLeft + previewOffset.x : baseLeft
    const previewTop = resizePreview ? baseTop + previewOffset.y : baseTop
    const previewWidth = resizePreview?.width ?? grid.width
    const previewHeight = resizePreview?.height ?? grid.height
    const previewRight = previewLeft + previewWidth * cellWithGap - 1
    const previewBottom = previewTop + previewHeight * cellWithGap - 1

    const corners: { corner: ResizeCorner; x: number; y: number; cursor: string }[] = [
      { corner: 'nw', x: baseLeft, y: baseTop, cursor: 'nwse-resize' },
      { corner: 'ne', x: baseRight, y: baseTop, cursor: 'nesw-resize' },
      { corner: 'sw', x: baseLeft, y: baseBottom, cursor: 'nesw-resize' },
      { corner: 'se', x: baseRight, y: baseBottom, cursor: 'nwse-resize' },
    ]

    // Corner positions that move with the preview
    const previewCorners: { corner: ResizeCorner; x: number; y: number }[] = [
      { corner: 'nw', x: previewLeft, y: previewTop },
      { corner: 'ne', x: previewRight, y: previewTop },
      { corner: 'sw', x: previewLeft, y: previewBottom },
      { corner: 'se', x: previewRight, y: previewBottom },
    ]

    const isVisible = showResizeAnchors || resizePreview

    return (
      <>
        {/* Invisible hover areas at corners to trigger anchor visibility */}
        {corners.map(({ corner, x, y, cursor }) => (
          <div
            key={`hover-${corner}`}
            className="absolute z-30"
            style={{
              left: x - hoverAreaSize / 2,
              top: y - hoverAreaSize / 2,
              width: hoverAreaSize,
              height: hoverAreaSize,
              cursor: isVisible ? cursor : 'default',
            }}
            onMouseEnter={() => {
              setShowResizeAnchors(true)
              setHoveredCorner(corner)
              if (hideAnchorsTimeoutRef.current) {
                clearTimeout(hideAnchorsTimeoutRef.current)
                hideAnchorsTimeoutRef.current = null
              }
            }}
            onMouseLeave={() => {
              if (!resizePreview) {
                hideAnchorsTimeoutRef.current = setTimeout(() => {
                  setShowResizeAnchors(false)
                  setHoveredCorner(null)
                }, 300)
              }
            }}
            onPointerDown={(e) => handleResizePointerDown(e, corner)}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
        ))}
        {/* Visible anchor circles - move with preview */}
        {isVisible && previewCorners.map(({ corner, x, y }) => (
          <div
            key={corner}
            className="absolute pointer-events-none z-20 transition-opacity rounded-full bg-black"
            style={{
              left: x - anchorSize / 2,
              top: y - anchorSize / 2,
              width: anchorSize,
              height: anchorSize,
              opacity: isVisible ? 1 : 0,
            }}
          />
        ))}
        {/* Grid border and dimension pills - shown on hover and during resize */}
        {isVisible && (
          <>
            {/* Border around grid */}
            <div
              className="absolute pointer-events-none z-10"
              style={{
                left: previewLeft,
                top: previewTop,
                width: previewWidth * cellWithGap - 1,
                height: previewHeight * cellWithGap - 1,
                border: resizePreview ? '1px dashed rgba(0, 0, 0, 0.5)' : '1px dashed rgba(0, 0, 0, 0.25)',
              }}
            />
            {/* Dimension pills - only shown during drag */}
            {resizePreview && (
              <>
                {/* Width pill - centered on horizontal edge */}
                <div
                  className="absolute pointer-events-none z-30 text-white text-xs font-medium px-2 py-0.5 rounded bg-black"
                  style={{
                    left: previewLeft + (previewWidth * cellWithGap) / 2,
                    top: resizePreview.corner.startsWith('n')
                      ? previewTop
                      : previewBottom,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  {previewWidth}
                </div>
                {/* Height pill - centered on vertical edge, rotated 90deg */}
                <div
                  className="absolute pointer-events-none z-30 text-white text-xs font-medium px-2 py-0.5 rounded bg-black"
                  style={{
                    left: resizePreview.corner.endsWith('w')
                      ? previewLeft
                      : previewRight,
                    top: previewTop + (previewHeight * cellWithGap) / 2,
                    transform: 'translate(-50%, -50%) rotate(-90deg)',
                  }}
                >
                  {previewHeight}
                </div>
              </>
            )}
          </>
        )}
      </>
    )
  }

  // Render floating save button above selected temp word
  const renderFloatingSaveButton = () => {
    if (!onSaveTempWord || selectedPlacementIds.size !== 1) return null

    const onlySelectedId = selectedPlacementIds.values().next().value as string
    const placement = grid.placements.find(p => p.id === onlySelectedId)
    if (!placement) return null

    // Check if this placement has temp words (not placeholders)
    const wordAIsPlaceholder = placement.wordAText.includes('□')
    const wordBIsPlaceholder = placement.wordBText.includes('□')
    const canSaveA = placement.isTempA && !wordAIsPlaceholder
    const canSaveB = placement.isTempB && !wordBIsPlaceholder

    if (!canSaveA && !canSaveB) return null

    // Calculate position above the placement
    const XY = calculateExtendedGridPosition(placement.row, placement.col)
    const wordLength = Math.max(
      placement.wordAText?.replace(/\s/g, '').length ?? 0,
      placement.wordBText?.replace(/\s/g, '').length ?? 0
    )
    const cellWithGap = cellSize + 1

    // Position above the word, centered horizontally
    const wordWidth = placement.direction === 'across' ? wordLength * cellWithGap : cellWithGap
    const buttonX = placement.direction === 'across'
      ? XY.x + wordWidth / 2
      : XY.x + cellWithGap / 2
    const buttonY = XY.y - 8 // 8px above the word

    const buttonLabel = canSaveA && canSaveB
      ? 'Save pair'
      : canSaveA
        ? `Save to ${listNames.A}`
        : `Save to ${listNames.B}`

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (canSaveA) onSaveTempWord(onlySelectedId, 'A')
      if (canSaveB) onSaveTempWord(onlySelectedId, 'B')
    }

    return (
      <button
        onClick={handleClick}
        className="absolute px-2 py-0.5 text-xs font-medium text-amber-800 bg-amber-200 hover:bg-amber-300 rounded shadow-sm whitespace-nowrap z-50 -translate-x-1/2 -translate-y-full"
        style={{
          left: buttonX,
          top: buttonY,
        }}
      >
        {buttonLabel}
      </button>
    )
  }

  // Main grid container with all layers (no separate interaction layer)
  // isolation: isolate creates a new stacking context for local z-index values
  const renderGrid = (showSide: 'A' | 'B' = 'A', showSaveButton: boolean = false, isActiveSelection: boolean = true) => (
    <div
      className="relative"
      style={{
        width: gridWidth,
        height: gridHeight,
        isolation: 'isolate',
      }}
    >
      {renderExtendedGridBackground()}
      {renderCellBackgrounds(showSide, isActiveSelection)}
      {renderScratchpadInteractionLayer()}
      {renderPlacements(showSide, isActiveSelection)}
      {renderPreviewPlacement(showSide)}
      {renderResizeAnchors()}
      {showSaveButton && renderFloatingSaveButton()}
    </div>
  )

  // Split view renders a unified grid with left half showing A, right half showing B
  const renderSplitView = () => {
    const isAActive = activeSplitSide === 'A'
    const isBActive = activeSplitSide === 'B'

    // Each half is centered on its side of the container
    return (
      <div
        ref={gridContainerRef}
        className="flex w-full h-full"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Left half - Word A */}
        <div
          className={`flex-1 relative overflow-hidden ${isAActive ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
          onClick={() => setActiveSplitSide('A')}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`text-xs font-medium absolute top-2 left-1/2 -translate-x-1/2 z-10 ${isAActive ? 'text-blue-500' : 'text-gray-400'}`}>{listNames.A}</div>
            <div
              ref={splitATransformRef}
              style={{
                transform: `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                willChange: isPanning ? 'transform' : 'auto',
              }}
            >
              {renderGrid('A', isAActive, isAActive)}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-300 shrink-0" />

        {/* Right half - Word B */}
        <div
          className={`flex-1 relative overflow-hidden ${isBActive ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
          onClick={() => setActiveSplitSide('B')}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`text-xs font-medium absolute top-2 left-1/2 -translate-x-1/2 z-10 ${isBActive ? 'text-blue-500' : 'text-gray-400'}`}>{listNames.B}</div>
            <div
              ref={splitBTransformRef}
              style={{
                transform: `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                willChange: isPanning ? 'transform' : 'auto',
              }}
            >
              {renderGrid('B', isBActive, isBActive)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Handle click on background to deselect
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (!onDeselect) return

    // Check if the click target is outside the grid content
    // Deselect if clicking on the container or any wrapper div, but not on cells or placements
    const target = e.target as HTMLElement

    // Don't deselect if clicking on a cell, placement, or control element
    if (target.closest('[data-grid-cell]') ||
        target.closest('[data-grid-placement]') ||
        target.closest('button') ||
        target.closest('input')) {
      return
    }

    // Deselect on clicks to the background area
    onDeselect()
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden bg-gray-100 relative select-none"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleBackgroundClick}
      style={{
        cursor: isPanning ? 'grabbing' : 'default',
        touchAction: 'none', // Prevent browser touch gestures
        overscrollBehavior: 'contain', // Contain overscroll to this element
      }}
    >

      {/* Overlay mode toggle */}
      {viewMode === 'overlay' && (
        <div className="absolute top-2 left-2 flex gap-1 p-0.5 bg-white rounded shadow-sm border border-gray-200" style={{ zIndex: gridControls }}>
          <button
            onClick={() => setOverlaySide('A')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${overlaySide === 'A'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:text-gray-900'
              }`}
            title="Show word A"
          >
            A
          </button>
          <button
            onClick={() => setOverlaySide('B')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${overlaySide === 'B'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:text-gray-900'
              }`}
            title="Show word B"
          >
            B
          </button>
        </div>
      )}

      {/* Main content based on view mode */}
      {viewMode === 'side-by-side' ? (
        renderSplitView()
      ) : (
        <div
          ref={setSingleTransformNode}
          className="absolute"
          style={{
            transform: `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            willChange: isPanning ? 'transform' : 'auto',
            left: '50%',
            top: '50%',
            marginLeft: -gridWidth / 2,
            marginTop: -gridHeight / 2
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {renderGrid(viewMode === 'overlay' ? overlaySide : 'A', true)}
        </div>
      )}

      {/* Help text */}
      <div className="absolute bottom-2 left-2 text-[10px] text-gray-400">
        {scratchpadMode
          ? 'Scratchpad • Scroll to pan • Pinch to zoom • Drag pairs freely • R rotate'
          : 'Scroll to pan • Pinch to zoom • Shift+click to block • R rotate • B block • Cmd/Ctrl+Z undo'}
      </div>
    </div>
  )
}
